import React, { useState, useCallback } from 'react';
import { useTheme } from '../App';

function PostDetails({ post, loading, onClose }) {
  const { theme } = useTheme();
  const [collapsedComments, setCollapsedComments] = useState({});

  const toggleCommentCollapse = (commentId) => {
    setCollapsedComments(prev => ({
      ...prev,
      [commentId]: !prev[commentId]
    }));
  };

  const processComments = useCallback((commentData, depth = 0) => {
    return commentData
      .filter(comment => comment.kind === 't1')
      .map(comment => {
        const data = comment.data;
        const processedComment = {
          id: data.id,
          author: data.author,
          body: data.body,
          score: data.score,
          created_utc: data.created_utc,
          depth: depth,
          replies: []
        };
        
        if (data.replies && data.replies.data && data.replies.data.children) {
          processedComment.replies = processComments(data.replies.data.children, depth + 1);
        }
        
        return processedComment;
      });
  }, []);

  const renderComment = (comment) => {
    const indent = comment.depth * 20;
    const borderColor = [
      theme.colors.accentCyan,
      theme.colors.accentGreen,
      theme.colors.accentOrange,
      theme.colors.accentPurple
    ][comment.depth % 4];
    
    const isCollapsed = collapsedComments[comment.id];

    return (
      <div key={comment.id} style={{ marginLeft: `${indent}px`, marginBottom: '10px' }}>
        <div style={{
          borderLeft: comment.depth > 0 ? `2px solid ${borderColor}` : 'none',
          paddingLeft: comment.depth > 0 ? '10px' : '0'
        }}>
          <div 
            style={{ 
              marginBottom: '5px', 
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}
            onClick={() => toggleCommentCollapse(comment.id)}
          >
            <span style={{ 
              color: theme.colors.accentGreen,
              fontSize: '12px',
              userSelect: 'none'
            }}>
              [{isCollapsed ? '+' : '-'}]
            </span>
            <span style={{ color: theme.colors.accentGreen }}>{comment.author}</span>
            <span style={{ color: theme.colors.accentOrange, fontSize: '12px' }}>
              {comment.score} points
            </span>
          </div>
          {!isCollapsed && (
            <>
              <div style={{ 
                color: theme.colors.foreground, 
                whiteSpace: 'pre-wrap',
                fontSize: '13px',
                lineHeight: '1.4'
              }}>
                {comment.body}
              </div>
              {comment.replies.map(reply => renderComment(reply))}
            </>
          )}
        </div>
      </div>
    );
  };

  const titleBarStyle = {
    backgroundColor: theme.colors.background2,
    padding: '10px 15px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: `1px solid ${theme.colors.border}`,
  };

  const processedComments = post.comments ? processComments(post.comments) : [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={titleBarStyle}>
        <span style={{ color: theme.colors.accentGreen, fontSize: '14px' }}>
          󰨞 Post Details - r/{post.subreddit}
        </span>
        <button
          onClick={onClose}
          style={{
            background: 'transparent',
            border: 'none',
            color: theme.colors.accentOrange,
            cursor: 'pointer',
            fontSize: '16px',
            padding: '0 5px',
          }}
        >
          ✕
        </button>
      </div>
      
      <div style={{ 
        flex: 1, 
        overflowY: 'auto', 
        padding: '20px',
        fontFamily: 'CaskaydiaCove Nerd Font, monospace',
      }}>
        {loading ? (
          <div style={{ color: theme.colors.accentCyan }}>Loading post details...</div>
        ) : (
          <>
            <h2 style={{ 
              color: theme.colors.foreground, 
              marginBottom: '10px',
              fontSize: '16px',
              lineHeight: '1.3'
            }}>
              {post.title}
            </h2>
            
            <div style={{ 
              marginBottom: '20px', 
              fontSize: '12px',
              color: theme.colors.accentOrange 
            }}>
              Posted by u/{post.author} • {post.score} points • {post.num_comments} comments
            </div>
            
            {post.is_self && post.selftext && (
              <div style={{ 
                whiteSpace: 'pre-wrap', 
                color: theme.colors.foreground,
                marginBottom: '30px',
                fontSize: '13px',
                lineHeight: '1.5',
                padding: '15px',
                backgroundColor: theme.colors.background2,
                borderLeft: `3px solid ${theme.colors.accentPurple}`
              }}>
                {post.selftext}
              </div>
            )}
            
            {!post.is_self && (
              <div style={{ marginBottom: '30px' }}>
                <a 
                  href={post.url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ 
                    color: theme.colors.accentCyan,
                    fontSize: '13px' 
                  }}
                >
                  {post.url}
                </a>
              </div>
            )}
            
            {processedComments.length > 0 && (
              <>
                <div style={{ 
                  color: theme.colors.accentCyan, 
                  marginBottom: '15px',
                  fontSize: '14px',
                  borderBottom: `1px solid ${theme.colors.border}`,
                  paddingBottom: '10px'
                }}>
                  COMMENTS ({processedComments.length})
                </div>
                {processedComments.map(comment => renderComment(comment))}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default PostDetails;